type BetterStackUser = {
  id: string;
  email?: string;
  username?: string;
  [key: string]: string | number | undefined;
};

type BetterStackFn = (
  command: string,
  ...args: unknown[]
) => void;

declare global {
  interface Window {
    betterstack?: BetterStackFn;
  }
}

const isBetterStackEnabled = () =>
  Boolean(process.env.REACT_APP_BETTERSTACK_TOKEN);

const buildTracePropagationTargets = (): (string | RegExp)[] => {
  const targets: (string | RegExp)[] = ['localhost', /^https?:\/\//];

  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    try {
      const { origin, hostname } = new URL(apiUrl);
      targets.push(origin);
      targets.push(new RegExp(`^${origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));
      if (hostname !== 'localhost') {
        targets.push(new RegExp(`^https?://${hostname.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));
      }
    } catch {
      // ignore invalid API URL
    }
  }

  targets.push(/^https:\/\/api\.argoli\.co/);
  targets.push(/^https:\/\/api\.dev\.argoli\.dev/);

  return targets;
};

const initBetterStack = () => {
  const token = process.env.REACT_APP_BETTERSTACK_TOKEN;
  if (!token) {
    return;
  }

  const betterstack: BetterStackFn = (...args) => {
    const queue = ((betterstack as BetterStackFn & { q?: unknown[] }).q =
      (betterstack as BetterStackFn & { q?: unknown[] }).q || []);
    queue.push(args);
  };
  (betterstack as BetterStackFn & { l?: number }).l = Date.now();
  window.betterstack = betterstack;

  const environment = process.env.REACT_APP_BETTERSTACK_ENV;
  const version = process.env.REACT_APP_VERSION;
  const buildId = process.env.REACT_APP_BUILD_ID;
  const release =
    version && buildId
      ? `${version}+${buildId}`
      : version || buildId || undefined;

  betterstack('config', {
    ...(environment ? { environment } : {}),
    ...(release ? { release } : {}),
    sentry: {
      ...(release ? { release } : {}),
      tracePropagationTargets: buildTracePropagationTargets(),
      propagateTraceparent: true,
    },
  });

  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://betterstack.net/b.js?t=${token}`;
  (document.head || document.getElementsByTagName('head')[0]).appendChild(script);

  betterstack('init', {
    ...(environment ? { environment } : {}),
    ...(release ? { release } : {}),
  });
};

export const setBetterStackUser = (user: BetterStackUser) => {
  if (!isBetterStackEnabled() || !window.betterstack) {
    return;
  }
  window.betterstack('user', user);
};

export const clearBetterStackUser = () => {
  if (!isBetterStackEnabled() || !window.betterstack) {
    return;
  }
  window.betterstack('user', null);
};

export default initBetterStack;
